import { Apple, Youtube, ArrowUpRight, Music2, Cloud, Mic } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { AlbumExperience } from "@/components/site/AlbumExperience";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  SOCIALS,
  SPOTIFY_ARTIST_URL,
  MUSIC_PLATFORM_LINKS,
  MUSIC_SUBTITLE,
  MUSIC_VIDEO_YOUTUBE_ID,
  MUSIC_VIDEO_TITLE,
  FEATURED_SONG,
  ALBUM,
  PODCAST_URL,
} from "@/data/site";

// Spotify's official embed (iframe) pulls discography directly from her
// artist profile and stays current automatically as she releases new music.
const spotifyEmbedSrc = (artistUrl) => {
  const match = artistUrl?.match(/artist\/([a-zA-Z0-9]+)/);
  return match ? `https://open.spotify.com/embed/artist/${match[1]}?theme=0` : null;
};

const PLATFORM_ICONS = {
  Spotify: Music2,
  "Apple Music": Apple,
  YouTube: Youtube,
  SoundCloud: Cloud,
};

const SOUNDCLOUD_URL = MUSIC_PLATFORM_LINKS.find((l) => l.label === "SoundCloud")?.href;

// Same two-column shell as the template's Music page, mirrored (video left,
// Spotify + album promo card right) so it doesn't read as a Sierra clone. Sierra's gated unreleased-video teaser and her
// Live Sessions / vlog slots are dropped — Jenn has one released video and
// no unreleased footage or live sessions yet. Her SoundCloud-hosted
// homepage song gets its own card instead.
export default function Music() {
  const embedSrc = spotifyEmbedSrc(SPOTIFY_ARTIST_URL);
  const isMobile = useIsMobile();

  return (
    <div className="starfield relative min-h-screen">
      <div className="ambient-tint-cycle fixed inset-0 z-0" style={{ animationDelay: "-4s" }} />
      <Navbar />
      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-32 pt-40 text-center">
        <Reveal>
          <span className="overline">Music</span>
          {/* Her own phrase for underground hip hop, from her story. */}
          <h1 className="font-display mt-5 text-5xl text-[var(--sm-text)] sm:text-6xl">Poetry in Motion</h1>
          <p className="font-body mx-auto mt-5 max-w-2xl text-sm font-light leading-relaxed text-white/60">{MUSIC_SUBTITLE}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {MUSIC_PLATFORM_LINKS.map((l) => {
              const Icon = PLATFORM_ICONS[l.label] || Music2;
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`music-platform-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-accent text-xs uppercase tracking-[0.15em] text-white/80 transition-all duration-300 hover:border-[var(--sm-gold)]/60 hover:text-[var(--sm-gold-light)]"
                >
                  <Icon size={14} /> {l.label}
                </a>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-16 grid grid-cols-1 gap-12 text-left lg:grid-cols-2 lg:gap-0">
            <div className="flex h-full flex-col lg:order-2 lg:border-l lg:border-white/10 lg:pl-10">
              <span className="overline">Top Tracks</span>
              {embedSrc && (
                <iframe
                  data-testid="spotify-embed"
                  title="Jenn Rock on Spotify"
                  src={embedSrc}
                  width="100%"
                  height={380}
                  style={{ borderRadius: "12px", marginTop: "1.5rem" }}
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              )}

              {/* "The Enlightenment" promo card — the same AlbumExperience
                  component as the /new-album page, scaled down. */}
              <div className="mt-8 flex flex-1 flex-col items-center justify-center rounded-xl border border-[var(--sm-gold)]/25 px-4 py-6 text-center">
                <AlbumExperience scale={0.4} compact mobile={isMobile} />
              </div>
            </div>

            <div className="lg:order-1 lg:pr-10">
              <span className="overline" style={{ color: "var(--sm-turquoise)" }}>
                Watch
              </span>
              <div className="mt-5">
                <h3 className="font-display text-lg text-white">Music Video</h3>
                {MUSIC_VIDEO_TITLE && <p className="font-body mt-1 text-xs font-light italic text-white/45">"{MUSIC_VIDEO_TITLE}"</p>}
                <div className="mt-3 overflow-hidden rounded-xl border border-white/10">
                  <iframe
                    data-testid="music-video-embed"
                    className="aspect-video w-full"
                    src={`https://www.youtube.com/embed/${MUSIC_VIDEO_YOUTUBE_ID}`}
                    title={MUSIC_VIDEO_TITLE ? `Jenn Rock — ${MUSIC_VIDEO_TITLE}` : "Jenn Rock — music video"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Her pick for the song that best represents her world —
                  lives on SoundCloud, so this links out rather than embeds
                  (the short link she gave can't be resolved to a SoundCloud
                  widget URL from this build; swap for an embed once the full
                  track URL is confirmed). */}
              <div className="mt-12 rounded-xl border border-[var(--sm-gold)]/25 bg-black/30 px-6 py-6">
                <span className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-[var(--sm-gold)]">The song that says it all</span>
                <h3 className="font-display mt-2 text-2xl text-white">{FEATURED_SONG.title}</h3>
                <p className="font-body mt-2 text-sm font-light text-white/55">If you only hear one Jenn Rock song, make it this one.</p>
                {SOUNDCLOUD_URL && (
                  <a
                    href={SOUNDCLOUD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="featured-song-soundcloud-link"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--sm-gold)] px-6 py-2.5 font-body text-sm font-medium text-black transition-colors hover:bg-[var(--sm-gold-light)]"
                  >
                    Listen on SoundCloud <ArrowUpRight size={15} />
                  </a>
                )}
              </div>

              <div className="mt-8 px-1">
                <span className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-[var(--sm-turquoise)]">The title single</span>
                <h3 className="font-display mt-2 text-xl text-white">{ALBUM.single}</h3>
                <p className="font-accent mt-2 text-lg italic leading-snug text-white/70">"{ALBUM.singleNote}"</p>
              </div>

              {PODCAST_URL && (
                <a
                  href={PODCAST_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 flex items-center gap-3 rounded-xl border border-white/15 px-5 py-4 text-white/80 transition-colors hover:border-[var(--sm-gold)]/60 hover:text-[var(--sm-gold-light)]"
                >
                  <Mic size={18} /> <span className="font-accent text-sm uppercase tracking-[0.18em]">Listen to the podcast</span>
                </a>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-20 flex flex-wrap items-center justify-center gap-7">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-accent text-xs uppercase tracking-[0.2em] text-white/55 transition-colors hover:text-[var(--sm-gold-light)]"
              >
                {s.label}
              </a>
            ))}
          </div>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
}
